import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import { Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import { ImportButton } from '@ui/actions'
import { useSnackbar } from '@ui/snackbar'
import { useModelInvalidation } from '@structuralPlanningHooks'
import { IFCRoofSlabParseReport, startIfcRoofSlabParse } from '@mutations'
import { getRoofSlabParseWarning } from './ifcParseReport'

interface Props {
  guids: string[]
  ifcFileId: string | null
  projectId: string
  onSuccess: () => void
}

const IFCParseAsRoofSlabForm = ({
  guids,
  ifcFileId,
  projectId,
  onSuccess,
}: Props): ReactElement => {
  const invalidateModel = useModelInvalidation()
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: parseAsRoofSlab, isLoading: isParsing } = useMutation({
    mutationFn: async () => {
      if (!projectId || !ifcFileId || guids.length === 0) return
      const configs = guids.map(ifc_element_id => ({
        ifc_element_id,
        ifc_file_id: ifcFileId,
      }))
      return startIfcRoofSlabParse.request(projectId, configs) as Promise<
        IFCRoofSlabParseReport | undefined
      >
    },
    onSuccess: async (report?: IFCRoofSlabParseReport) => {
      if (projectId) await invalidateModel(projectId)
      const warning = getRoofSlabParseWarning(report)
      if (warning) enqueueSnackbar(warning, { variant: 'warning' })
      else
        enqueueSnackbar(
          `${guids.length > 1 ? 'Dachflächen' : 'Dachfläche'} erfolgreich importiert`,
          {
            variant: 'success',
          },
        )
      onSuccess()
    },
    onError: async (error: AxiosError) => {
      enqueueSnackbar(
        buildErrorMessage(error, 'Fehler beim Dachflächenimport. Bitte Modell kontrollieren.'),
        {
          variant: 'error',
        },
      )
      if (projectId) await invalidateModel(projectId)
    },
  })

  const canParse = Boolean(projectId && ifcFileId && guids.length > 0)

  return (
    <Stack spacing={1}>
      <ImportButton
        variant="contained"
        onClick={() => parseAsRoofSlab()}
        disabled={!canParse}
        loading={isParsing}
        size="small"
        fullWidth
      >
        {guids.length > 1
          ? `${guids.length} Dachflächen importieren`
          : 'als Dachfläche importieren'}
      </ImportButton>
    </Stack>
  )
}

export default IFCParseAsRoofSlabForm
